import { Credits } from "./credits";
import { Manufacturer } from "./manufacturer";
import {
  EuRoHSStatus,
  ObsolescencelifeCycleRisk,
  ObsolescenceStatus,
  ReachAffectedStatus,
  WITHOUT_DATA,
} from "./rm-common-properties";

export type SapItemCategory = "L" | "O" | "S" | "X";

/** BOM-level properties of a child part in an assembly. */
export interface BomPartEntry {
  quantity: number;
  designator?: string;
  sapItemCategory?: SapItemCategory;
}

/**
 * Numeric values are retained for backwards compatibility with existing part
 * documents. New or edited BOM entries use BomPartEntry.
 */
export type PartRelation = number | BomPartEntry;

export interface Parts {
  [partNumber: string]: PartRelation;
}

export function getBomPartEntry(
  value: PartRelation | null | undefined
): BomPartEntry {
  if (typeof value === "number") {
    return { quantity: value };
  }
  if (value == null) {
    return { quantity: 1 };
  }
  return {
    ...value,
    quantity: Number(value.quantity) || 1
  };
}

export function getBomPartQuantity(
  value: PartRelation | null | undefined
): number {
  return getBomPartEntry(value).quantity;
}

export class Turnover {
  calendarYear: string = "";
  turnover: string = "";
  pricePerItem?: string = "";
  totalAmount?: string = "";
}
export class Turnovers {
  year: string = "";
  turnover: string = "";
}

export class PartData extends Credits {
  partNumber: string = "";
  description: string = "";
  parts: Parts = {};
  level: string = "";
  responsibleName?: string = "";
  responsibleDepartment?: string = "";
  productCategory?: string = "";
  productAcronym?: string = "";
  productOwner?: string = "";
  productPmName?: string = "";
  productPm?: string = "";
  productStatus?: string = "";
  productSOP?: number = 0;
  productEOP?: number = 0;
  forecast?: string = "";
  purchaseId?: string = "";
  usedInPartNumber?: string = "";
  usedInQuantity?: string = "";
  sapDescription?: string = "";
  turnover?: Turnover[] = [];
  turnOverQuantity?: string = "";
  turnOverPrice?: string = "";
  turnOverCurrentYear?: string = "";
  equivalentInformation?: string = "";
  productNumber?: string = "";
  productName?: string = "";
  obsolescenceManagement?: boolean = false;
  open?: boolean = false;
  seen?: boolean = false;
  quantity?: string = "";
  itemType?: string = "";
  classNumber?: string = "";
  usedInSystem?: string = "";
  norm?: string = "";
  readilyAvailableStock?: string = "";
  actualStock?: string = "";
  safetyStock?: string = "";
  totalLifeCycleUsage?: string = "";
  totalForecast2Years?: string = "";
  forecastEOLStock?: string = "";
  stockLevelDeclination?: string = "";
  purchasePartNumber?: string = "";
  MPNAssembly?: string = "";
  historyFields?: string[] = [];
  numberSecSource?: string = "";
  likelihood_secSource?: string = "";
  freqUse?: string = "";
  freqUseImpact?: string = "";
  sapStatus?: string = "";
  designType?: string = "";
  catComplexity?: string = "";
  catComplexityImpact?: string = "";
  posType?: string = "";
  matType?: string = "";
  stockPAS?: string = "";
  stockPS2?: string = "";
  stockMS5?: string = "";
  stockFrom?: number = 0;
  stockRangeYears?: string = "";
  stockRange?: number = -1;
  equipmentType?: string = "";
  equipmentDescription?: string = "";
  outflows?: string = "";
  outflows5years?: string = "";
  sapComments?: string = "";
  config?: string = "";
  emissionEMREL?: string = "";
  nuclearPower?: string = "";
  responsibleOM?: string = "";
  responsibleRD?: string = "";
  partner?: string = "";
  product?: string = "";
  startSerie?: number = 0;
  endSerie?: number = 0;
  endDelivery?: number = 0;
  startRedesign?: string = "";
  electronicalMat?: string = "";
  euRoHS?: EuRoHSStatus = "";
  obsolescenceStatus?: ObsolescenceStatus = WITHOUT_DATA;
  obsolescenceStatus2years?: ObsolescenceStatus = WITHOUT_DATA;
  obsolescenceStatus4years?: ObsolescenceStatus = WITHOUT_DATA;
  obsolescenceStatus6years?: ObsolescenceStatus = WITHOUT_DATA;
  obsolescenceStatus8years?: ObsolescenceStatus = WITHOUT_DATA;
  likelihood?: ObsolescencelifeCycleRisk = "";
  impact?: ObsolescencelifeCycleRisk = "";
  totalRisk?: ObsolescencelifeCycleRisk = "";
  reachSubstances?: string[] = [];
  reachAffected?: ReachAffectedStatus = "";
  leadTime?: ObsolescencelifeCycleRisk = "";
  topLevel?: boolean = false;
  inventoryMonitoring?: boolean = false;
  hazardousSubstance: string = "";
  productSafety: string = "";
  extent: string = "";
  componentApproval: string = "";
  exemption: string[] = [];
  devStatus: string = "";
  statusAlerts?: StatusAlert = new StatusAlert();
  turnovers?: Turnovers[] = [];
  category?: string = "";
  activeMPNs?: number = 0;
  stockRangeUntil?: number = 0;
  manualMonitoringRange?: number = 0;
  standardRange: number = 0;
  monitoringRangeUpTo?: number = 0;
  globalStock?: string = "";
  minimumStock?: number = 0;
  verwa?: string = "";
  productHierarchy?: string = "";
  lastImportTime?: number = 0;
  warehouseGroupId?: string = "";
  warehouseImpact?: ObsolescencelifeCycleRisk = "";
  warehouseRotation?: number = 0;
  stock?: number = 0;
  consumption?: number = 0;
  maxWeeks?: string = "";
  totalMarketInv?: string = "";
  distributor?: string = "";
  countryOfOrigin?: string = "";
  manualObsolescenceManagement?: boolean = false;
  materialShortText?: string = "";
  departmentName?: string = "";
  contactPoint?: string = "";
  laboratory?: string = "";
  plannedDeliveryTime?: number = 0;
  fixedGoodsReceipts?: number = 0;
  plannedGoodsReceipts?: number = 0;
  fixedRequirements?: number = 0;
  plannedRequirements?: number = 0;
  rangeWithInventory?: number = 0;
  active?: boolean = true;
}

export class Part extends PartData {
  [key: string]: any;
  _id: string = ""; // mandatory id (equals part number)
}
export interface Row {
  [key: string]: any;
  level: number;
  quantity?: number;
  node?: PartNode;
  manufacturer?: Manufacturer;
}

export interface PartNode {
  part: Partial<Part>;
  bom: { node: PartNode; quantity?: number }[];
}

export class StatusAlert {
  currentStatusCounter = 0;
  riskCounter = 0;
  likelihoodCounter = 0;
  impactCounter = 0;
  rohsCounter = 0;
  reachCounter = 0;
  leadTimeCounter = 0;
}
